import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ExternalLink, Filter, X, Check, AlertCircle } from "lucide-react";
import { beverageList, beveragePatch, beverageBulk } from "../../lib/adminApi";

const CATEGORIES = [
  "whisky", "rum", "tequila", "mezcal", "vodka", "liqueurs",
  "agave", "aperitifs", "wine", "beer", "sake", "vermouth", "amaro",
];

const AVAILABILITY_TONE = {
  AVAILABLE: "border-green-700/40 text-green-400",
  NOT_AVAILABLE: "border-amber-600/40 text-amber-400",
  HIDDEN: "border-white/15 text-white/40",
};

const PRICING_BADGE = {
  POUR_ONLY: { label: "Pour", tone: "border-white/15 text-white/70" },
  BOTTLE_ONLY: { label: "Bottle only", tone: "border-[#C17F59]/40 text-[#C17F59]" },
  BOTH: { label: "Both", tone: "border-green-700/40 text-green-400" },
};

const MODE_FILTERS = [
  { k: "All", l: "All pricing modes" },
  { k: "POUR_ONLY", l: "Pour only" },
  { k: "BOTTLE_ONLY", l: "Bottle only" },
  { k: "BOTH", l: "Both" },
  { k: "missing_30ml", l: "Missing 30ml price" },
  { k: "missing_60ml", l: "Missing 60ml price" },
  { k: "missing_any_price", l: "Missing any price" },
];

export default function AdminBeverages() {
  const [bevs, setBevs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [avail, setAvail] = useState("All");
  const [modeFilter, setModeFilter] = useState("All");
  const [selected, setSelected] = useState(() => new Set());
  const [toast, setToast] = useState(null);

  const reload = async () => {
    const d = await beverageList({ limit: 2000 });
    setBevs(d.beverages || []);
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return bevs.filter((b) => {
      if (cat !== "All" && b.category !== cat) return false;
      if (avail !== "All" && b.availability_status !== avail) return false;
      const mode = b.pricing_mode || "POUR_ONLY";
      if (["POUR_ONLY", "BOTTLE_ONLY", "BOTH"].includes(modeFilter) && mode !== modeFilter) return false;
      if (modeFilter === "missing_30ml" && b.price_30ml != null) return false;
      if (modeFilter === "missing_60ml" && b.price_60ml != null) return false;
      if (modeFilter === "missing_any_price" &&
          (b.price_30ml || b.price_60ml || b.price_by_bottle)) return false;
      if (term) {
        const hay = `${b.display_name} ${b.brand} ${b.expression || ""} ${b.id}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [bevs, q, cat, avail, modeFilter]);

  const applyPatch = async (id, patch) => {
    const prev = bevs.find((b) => b.id === id);
    // Optimistic update
    setBevs((arr) => arr.map((b) => b.id === id ? { ...b, ...patch } : b));
    try {
      const updated = await beveragePatch(id, patch);
      setBevs((arr) => arr.map((b) => b.id === id ? updated : b));
      setToast({ kind: "ok", msg: "Saved" });
    } catch (e) {
      // Rollback
      setBevs((arr) => arr.map((b) => b.id === id ? prev : b));
      setToast({ kind: "err", msg: e.response?.data?.detail || "Save failed" });
    }
  };

  const toggleSelect = (id) =>
    setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selectAllVisible = (on) =>
    setSelected((s) => on ? new Set(filtered.map((b) => b.id)) : new Set());

  const counts = useMemo(() => ({
    all: bevs.length,
    AVAILABLE: bevs.filter((b) => b.availability_status === "AVAILABLE").length,
    NOT_AVAILABLE: bevs.filter((b) => b.availability_status === "NOT_AVAILABLE").length,
    HIDDEN: bevs.filter((b) => b.availability_status === "HIDDEN").length,
  }), [bevs]);

  const runBulk = async (action, value, label) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`You are about to ${label} ${ids.length} selected item${ids.length === 1 ? "" : "s"}.\nContinue?`)) return;
    try {
      await beverageBulk(ids, action, value);
      setToast({ kind: "ok", msg: `${label} applied to ${ids.length} bottle${ids.length === 1 ? "" : "s"}` });
      setSelected(new Set());
      await reload();
    } catch (e) {
      setToast({ kind: "err", msg: e.response?.data?.detail || "Bulk action failed" });
    }
  };

  // Auto-clear toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  return (
    <div className="px-5 md:px-10 py-8 max-w-[1400px]" data-testid="admin-beverages">
      <header className="mb-6">
        <p className="overline text-[#C17F59] mb-2">Menu Manager · Spirits shelf</p>
        <h1 className="font-display text-4xl">{bevs.length} bottle{bevs.length === 1 ? "" : "s"} · spreadsheet view</h1>
        <p className="text-white/55 text-sm mt-2 max-w-2xl">
          Inline edits save instantly on blur. Bulk-select rows for batch actions. Open a row's editor for deeper SKU details (region, notes, tags).
          Gin lives under <Link to="/admin/menu" className="text-[#C17F59] underline underline-offset-4">Gin Menu</Link>.
        </p>
      </header>

      {/* Availability tabs */}
      <div className="flex flex-wrap gap-1 mb-3 border-b border-white/8 overflow-x-auto no-scrollbar" data-testid="bev-availability-chips">
        {[
          { k: "All", l: `All (${counts.all})` },
          { k: "AVAILABLE", l: `Available (${counts.AVAILABLE})` },
          { k: "NOT_AVAILABLE", l: `Not Available (${counts.NOT_AVAILABLE})` },
          { k: "HIDDEN", l: `Hidden (${counts.HIDDEN})` },
        ].map((c) => (
          <button key={c.k} onClick={() => setAvail(c.k)}
                  className={`text-[11px] uppercase tracking-[0.22em] px-3 py-2 transition-colors ${avail === c.k ? "text-[#C17F59] border-b border-[#C17F59]" : "text-white/50 hover:text-white"}`}
                  data-testid={`bev-avail-chip-${c.k}`}>
            {c.l}
          </button>
        ))}
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap gap-3 mb-3 items-center">
        <div className="flex items-center gap-2 border-b border-white/15 px-2 py-2 flex-1 min-w-[260px]">
          <Search size={14} className="text-white/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by brand, expression, id..."
                 className="bg-transparent text-white w-full text-sm focus:outline-none placeholder:text-white/30"
                 data-testid="bev-search" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)}
                className="bg-[#15171A] text-white border border-white/10 px-3 py-2 text-sm focus:outline-none"
                data-testid="bev-filter-category">
          <option value="All" className="bg-[#15171A] text-white">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c} className="bg-[#15171A] text-white">{c}</option>)}
        </select>
        <select value={modeFilter} onChange={(e) => setModeFilter(e.target.value)}
                className="bg-[#15171A] text-white border border-white/10 px-3 py-2 text-sm focus:outline-none"
                data-testid="bev-filter-mode">
          {MODE_FILTERS.map((m) => <option key={m.k} value={m.k} className="bg-[#15171A] text-white">{m.l}</option>)}
        </select>
        <Link to="/admin/menu-upload"
              className="inline-flex items-center gap-2 border border-white/15 text-white/80 hover:text-[#C17F59] hover:border-[#C17F59] px-4 py-2 text-xs uppercase tracking-[0.22em]"
              data-testid="bev-upload-cta">
          <Filter size={12} /> Import via menu upload
        </Link>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 bg-[#1d2024] border border-[#C17F59]/40 px-4 py-3 mb-3" data-testid="bev-bulk-bar">
          <span className="overline text-[#C17F59] mr-2">{selected.size} selected</span>
          <button onClick={() => setSelected(new Set())} className="text-[10px] uppercase tracking-[0.2em] text-white/45 hover:text-white inline-flex items-center gap-1" data-testid="bulk-clear">
            <X size={11} /> Clear
          </button>
          <span className="text-white/20">|</span>
          <button onClick={() => runBulk("set_availability", "AVAILABLE", "set Available")}
                  className="text-[10px] uppercase tracking-[0.2em] text-green-400 hover:text-green-300 border border-green-700/40 hover:border-green-500 px-3 py-1.5"
                  data-testid="bulk-set-available">
            Set Available
          </button>
          <button onClick={() => runBulk("set_availability", "NOT_AVAILABLE", "set Not Available")}
                  className="text-[10px] uppercase tracking-[0.2em] text-amber-400 hover:text-amber-300 border border-amber-600/40 hover:border-amber-500 px-3 py-1.5"
                  data-testid="bulk-set-not-available">
            Set Not Available
          </button>
          <button onClick={() => runBulk("set_availability", "HIDDEN", "hide from public")}
                  className="text-[10px] uppercase tracking-[0.2em] text-white/55 hover:text-white border border-white/20 hover:border-white/40 px-3 py-1.5"
                  data-testid="bulk-set-hidden">
            Hide from public
          </button>
          <BulkCategoryPicker onPick={(c) => runBulk("set_category", c, `move to ${c}`)} />
          <button onClick={() => runBulk("delete", null, "permanently delete")}
                  className="text-[10px] uppercase tracking-[0.2em] text-red-400 hover:text-red-300 border border-red-500/30 hover:border-red-500/60 px-3 py-1.5"
                  data-testid="bulk-delete">
            Delete permanently
          </button>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 text-sm border ${
          toast.kind === "ok" ? "border-green-500/40 text-green-400 bg-green-500/10" : "border-red-500/40 text-red-400 bg-red-500/10"
        }`} data-testid="bev-toast">
          {toast.kind === "ok" ? <Check size={13} className="inline mr-1" /> : <AlertCircle size={13} className="inline mr-1" />}
          {toast.msg}
        </div>
      )}

      {loading && <p className="text-white/40">Loading...</p>}

      {!loading && bevs.length === 0 && (
        <div className="bg-[#15171A] border border-white/8 p-8 text-center" data-testid="bev-empty-state">
          <p className="overline text-white/40 mb-3">Shelf is empty</p>
          <p className="text-white/70 mb-4">No non-gin beverages have been imported or added yet.</p>
          <Link to="/admin/menu-upload" className="inline-flex items-center gap-2 bg-[#C17F59] text-[#0E0F0C] px-4 py-2 text-xs uppercase tracking-[0.22em] hover:bg-[#D89A74]">
            Upload a menu →
          </Link>
        </div>
      )}

      {!loading && bevs.length > 0 && (
        <div className="bg-[#15171A] border border-white/8 overflow-x-auto" data-testid="bev-table-wrapper">
          <table className="w-full text-sm" data-testid="admin-beverages-table">
            <thead>
              <tr className="border-b border-white/8 text-left sticky top-0 bg-[#15171A] z-10">
                <th className="py-3 px-3 w-8">
                  <input type="checkbox" data-testid="select-all-visible"
                         checked={selected.size > 0 && filtered.every((b) => selected.has(b.id))}
                         onChange={(e) => selectAllVisible(e.target.checked)} />
                </th>
                <th className="py-3 px-3 overline text-white/40">Name</th>
                <th className="py-3 px-3 overline text-white/40">Category</th>
                <th className="py-3 px-3 overline text-white/40">Mode</th>
                <th className="py-3 px-3 overline text-white/40 text-right">30ml</th>
                <th className="py-3 px-3 overline text-white/40 text-right">60ml</th>
                <th className="py-3 px-3 overline text-white/40 text-right">Bottle</th>
                <th className="py-3 px-3 overline text-white/40 text-right">ABV</th>
                <th className="py-3 px-3 overline text-white/40">Availability</th>
                <th className="py-3 px-3 overline text-white/40">Vis</th>
                <th className="py-3 px-3 overline text-white/40"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <BeverageRow key={b.id} b={b}
                             selected={selected.has(b.id)}
                             onSelect={() => toggleSelect(b.id)}
                             onPatch={(patch) => applyPatch(b.id, patch)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && bevs.length > 0 && filtered.length === 0 && (
        <p className="text-white/40 text-xs uppercase tracking-[0.2em] mt-4" data-testid="bev-no-results">
          No bottles match the current filters.
        </p>
      )}
    </div>
  );
}

function BulkCategoryPicker({ onPick }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)}
              className="text-[10px] uppercase tracking-[0.2em] text-white/65 hover:text-[#C17F59] border border-white/15 hover:border-[#C17F59] px-3 py-1.5"
              data-testid="bulk-set-category">
        Set category ↓
      </button>
      {open && (
        <div className="absolute z-30 mt-1 bg-[#101113] border border-white/15 grid grid-cols-2 min-w-[260px]">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => { setOpen(false); onPick(c); }}
                    className="text-[11px] text-left px-3 py-2 text-white/80 hover:bg-[#C17F59]/20 capitalize">
              {c}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function BeverageRow({ b, selected, onSelect, onPatch }) {
  const mode = b.pricing_mode || "POUR_ONLY";
  const badge = PRICING_BADGE[mode] || PRICING_BADGE.POUR_ONLY;
  return (
    <tr className={`border-b border-white/4 hover:bg-white/3 transition-colors ${selected ? "bg-[#C17F59]/8" : ""} ${b.availability_status === "HIDDEN" ? "opacity-55" : ""}`}
        data-testid={`bev-row-${b.id}`}>
      <td className="py-2 px-3">
        <input type="checkbox" checked={selected} onChange={onSelect} data-testid={`row-select-${b.id}`} />
      </td>
      <td className="py-2 px-3">
        <InlineText value={b.display_name || ""} onSave={(v) => onPatch({ display_name: v })} testid={`row-name-${b.id}`} />
        <p className="text-[9px] uppercase tracking-[0.2em] text-white/35 mt-0.5">{b.brand}{b.country ? ` · ${b.country}` : ""}</p>
      </td>
      <td className="py-2 px-3">
        <select value={b.category} onChange={(e) => onPatch({ category: e.target.value })}
                data-testid={`row-category-${b.id}`}
                className="bg-transparent text-white border-b border-transparent hover:border-white/15 focus:border-[#C17F59] focus:outline-none text-sm py-1 px-1 capitalize">
          {CATEGORIES.map((c) => <option key={c} value={c} className="bg-[#15171A] text-white">{c}</option>)}
        </select>
      </td>
      <td className="py-2 px-3">
        <span className={`text-[9px] uppercase tracking-[0.2em] border px-2 py-1 ${badge.tone}`} data-testid={`row-mode-${b.id}`}>
          {badge.label}
        </span>
      </td>
      <td className="py-2 px-3 text-right">
        <InlinePrice value={b.price_30ml} onSave={(v) => onPatch({ price_30ml: v })} testid={`row-p30-${b.id}`} />
      </td>
      <td className="py-2 px-3 text-right">
        <InlinePrice value={b.price_60ml} onSave={(v) => onPatch({ price_60ml: v })} testid={`row-p60-${b.id}`} />
      </td>
      <td className="py-2 px-3 text-right">
        <InlinePrice value={b.price_by_bottle} onSave={(v) => onPatch({ price_by_bottle: v })} testid={`row-pbtl-${b.id}`} />
      </td>
      <td className="py-2 px-3 text-right">
        <InlinePrice value={b.abv} onSave={(v) => onPatch({ abv: v })} suffix="%" testid={`row-abv-${b.id}`} />
      </td>
      <td className="py-2 px-3">
        <select value={b.availability_status || "AVAILABLE"}
                onChange={(e) => onPatch({ availability_status: e.target.value })}
                className={`bg-[#101113] border px-2 py-1 text-[10px] uppercase tracking-[0.18em] focus:outline-none ${AVAILABILITY_TONE[b.availability_status || "AVAILABLE"]}`}
                data-testid={`row-avail-${b.id}`}>
          <option value="AVAILABLE" className="bg-[#15171A] text-white">Avail</option>
          <option value="NOT_AVAILABLE" className="bg-[#15171A] text-white">N/A</option>
          <option value="HIDDEN" className="bg-[#15171A] text-white">Hidden</option>
        </select>
      </td>
      <td className="py-2 px-3">
        <select value={b.visibility || "public"}
                onChange={(e) => onPatch({ visibility: e.target.value })}
                className="bg-transparent text-white/65 text-[10px] uppercase tracking-[0.18em] border border-white/10 hover:border-white/25 focus:border-[#C17F59] focus:outline-none px-2 py-1"
                data-testid={`row-vis-${b.id}`}>
          <option value="public" className="bg-[#15171A] text-white">Public</option>
          <option value="admin_only" className="bg-[#15171A] text-white">Admin</option>
        </select>
      </td>
      <td className="py-2 px-3 text-right whitespace-nowrap">
        <a href={`/spirits/item/${b.id}`} target="_blank" rel="noreferrer"
           className="inline-flex items-center text-white/40 hover:text-white mr-3" title="View public" data-testid={`row-public-${b.id}`}>
          <ExternalLink size={12} />
        </a>
        <Link to={`/admin/beverages/${b.id}`}
              className="text-[10px] uppercase tracking-[0.18em] text-[#C17F59] hover:text-[#D89A74]"
              data-testid={`row-details-${b.id}`}>
          Details
        </Link>
      </td>
    </tr>
  );
}

function InlineText({ value, onSave, testid }) {
  const [v, setV] = useState(value ?? "");
  const ref = useRef(value ?? "");
  useEffect(() => { setV(value ?? ""); ref.current = value ?? ""; }, [value]);
  const commit = () => { if (v !== ref.current) { ref.current = v; onSave(v); } };
  return (
    <input value={v} onChange={(e) => setV(e.target.value)}
           onBlur={commit}
           onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setV(ref.current); }}
           className="bg-transparent border-b border-transparent hover:border-white/15 focus:border-[#C17F59] focus:outline-none text-white text-sm py-1 px-1 w-full"
           data-testid={testid} />
  );
}

function InlinePrice({ value, onSave, suffix = "", testid }) {
  const [v, setV] = useState(value == null ? "" : String(value));
  const ref = useRef(value);
  useEffect(() => { setV(value == null ? "" : String(value)); ref.current = value; }, [value]);
  const commit = () => {
    const next = v === "" ? null : Number(v);
    if (next !== ref.current && !(next == null && ref.current == null) && !Number.isNaN(next)) {
      ref.current = next;
      onSave(next);
    }
  };
  return (
    <div className="inline-flex items-center justify-end gap-1">
      <span className="text-white/35 text-xs">{value == null ? "" : "₹"}</span>
      <input type="number" value={v} onChange={(e) => setV(e.target.value)}
             onBlur={commit}
             onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setV(value == null ? "" : String(value)); }}
             placeholder="—"
             className="bg-transparent border-b border-transparent hover:border-white/15 focus:border-[#C17F59] focus:outline-none text-white text-sm py-1 px-1 w-20 text-right tabular-nums placeholder:text-white/25"
             data-testid={testid} />
      {suffix && value != null ? <span className="text-white/35 text-xs">{suffix}</span> : null}
    </div>
  );
}
